import { useMemo, useState, useCallback, useEffect } from 'react';
import PageWrapper from '../../../components/Display/PageWrapper';
import ChartContainer from './ChartContainer';
import styles from '../../../components/Display/Wrapper.module.scss';
import AnalyticsSection from '../../../components/Display/AnalyticsSection';
import SmallerDataItem from './SmallerDataItem';
import { useAccountContext } from '../../../contexts/AccountContext';
import { privateFetch } from '../../../utils/apiHelper';

const SECTION_LABELS = {
  general: 'Vue générale',
  secondary: 'Analyse des sessions',
  summary: 'En bref'
};

const DISABLED_VALUES_BREF = [
  {
    label: 'Nombre de pages visitées',
    format: 'number',
    value: 3241
  },
  {
    label: 'Nombre de sessions uniques',
    format: 'number',
    value: 1288
  },
  {
    label: 'Temps de session moyen',
    format: 'duration',
    value: 1299888
  }
];

const DISABLED_VALUES_SECONDARY = [
  {
    config: {
      key: 'browser-usage',
      label: 'Répartition par navigateur',
      endPoint: 'browser',
      chartType: 'pieChart',
      displayType: 'secondary'
    },
    data: {
      labels: ['Chrome', 'Safari', 'Firefox', 'Edge', 'Autres'],
      datasets: [{ data: [1562, 501, 1280, 120, 100] }]
    }
  },

  {
    config: {
      key: 'device-usage',
      label: 'Répartition des appareils utilisés',
      endPoint: 'device',
      chartType: 'pieChart',
      displayType: 'secondary',
      info: 'Répartition des appareils utilisés lors des différentes visites'
    },
    data: {
      labels: ['Mobile', 'Ordinateur', 'Tablette', 'Autre'],
      datasets: [{ data: [1562, 2001, 300, 100] }]
    }
  },
  {
    config: {
      key: 'language-usage',
      label: 'Répartition par langue',
      endPoint: 'languages',
      chartType: 'pieChart',
      displayType: 'secondary'
    },
    data: {
      labels: ['Français', 'Anglais', 'Autres'],
      datasets: [{ data: [1562, 201, 13] }]
    }
  },
  {
    config: {
      key: 'rebound-page',
      label: 'Page de fin de session',
      endPoint: 'rebound',
      chartType: 'pieChart',
      displayType: 'secondary'
    },
    data: {
      labels: ['Accueil', 'Contact', 'Projets', 'A propos'],
      datasets: [{ data: [1562, 1100, 201, 13] }]
    }
  },
  {
    config: {
      key: 'day-of-week',
      label: 'Jours des visites',
      endPoint: 'day_of_week',
      chartType: 'barChart',
      displayType: 'secondary'
    },
    data: {
      labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
      datasets: [{ data: [1562, 501, 1280, 1200, 1000, 400, 250] }]
    }
  },
  {
    config: {
      key: 'hour-of-day',
      label: 'Heures des visites',
      endPoint: 'day_of_week',
      chartType: 'barChart',
      displayType: 'secondary'
    },
    data: {
      labels: ['00h-04h', '04h-08h', '08h-12h', '12h-16h', '16h-20h', '20h-24h'],
      datasets: [{ data: [140, 142, 1190, 1200, 1000, 400, 250] }]
    }
  }
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Helper pour calculer les dates selon la temporalité
const getDateRange = (temporality) => {
  const end = new Date();
  const start = new Date();

  switch (temporality) {
    case 'week':
      start.setDate(end.getDate() - 7);
      break;
    case 'month':
      start.setMonth(end.getMonth() - 1);
      break;
    case 'semester':
      start.setMonth(end.getMonth() - 6);
      break;
    case 'year':
      start.setFullYear(end.getFullYear() - 1);
      break;
    case 'all':
    default:
      // Pour 'all', on ne passe pas le paramètre start_dt
      return { start: null, end: null };
  }

  return {
    start: formatDate(start),
    end: formatDate(end)
  };
};

function AnalyticsPage({ isActive, config, disable, wip }) {
  const { accountContext } = useAccountContext();
  const [temporality, setTemporality] = useState('all');
  const [analyticsData, setAnalyticsData] = useState({}); // Stocke les données de tous les graphes
  const [loading, setLoading] = useState(false);

  // 1. Calcul des dates quand la temporalité change
  const { start, end } = useMemo(() => getDateRange(temporality), [temporality]);

  // 2. Fonction de fetch centralisée
  const fetchAllData = useCallback(async () => {
    if (disable || wip || !isActive || !config || config.length === 0) return;

    setLoading(true);

    try {
      const promises = config.map(async (c) => {
        const key = c.key;

        let url = `/cu/${accountContext.id}/${c.endPoint}/?scope=${c.scope}`;

        if (c.path) {
          url += `&path=${encodeURIComponent(c.path)}`;
        }

        if (start) {
          url += `&start_dt=${start}`;
        }

        if (end) {
          url += `&end_dt=${end}`;
        }

        const response = await privateFetch('GET', 'analytics', url);

        // Selon l'implémentation de privateFetch :
        // - fetch classique => response peut déjà être le JSON
        // - axios => response.data contient le JSON
        const data = response?.data ?? response;
        return {
          key,
          data,
          config: c
        };
      });

      const results = await Promise.all(promises);

      const newDataMap = {};

      results.forEach(({ key, data, config }) => {
        newDataMap[key] = {
          data,
          config
        };
      });

      setAnalyticsData(newDataMap);
    } catch (error) {
      console.error('Erreur lors du chargement des analytics', error);
    } finally {
      setLoading(false);
    }
  }, [config, accountContext.id, start, end, isActive, disable, wip]);

  // 3. Déclenchement du fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const groupedCharts = useMemo(() => {
    if (!config) return {};

    const groups = {};

    config.forEach((c) => {
      const type = c.displayType || 'general'; // Valeur par défaut si non défini

      if (!groups[type]) {
        groups[type] = [];
      }

      const key = c.key;
      const chartData = analyticsData[key];
      const isChartLoading = loading || !chartData;

      groups[type].push({
        ...c,
        data: chartData?.data,
        isLoading: isChartLoading
      });
    });

    return groups;
  }, [config, analyticsData, loading]);

  const temporalityActions = [
    { label: '7 jours', isSelected: temporality === 'week', onClick: () => setTemporality('week') },
    {
      label: '1 mois',
      isSelected: temporality === 'month',
      onClick: () => setTemporality('month')
    },
    {
      label: '6 mois',
      isSelected: temporality === 'semester',
      onClick: () => setTemporality('semester')
    },
    { label: '12 mois', isSelected: temporality === 'year', onClick: () => setTemporality('year') },
    {
      label: 'Depuis le début',
      isSelected: temporality === 'all',
      onClick: () => setTemporality('all')
    }
  ];

  // WIP ANALYTICS PAGE
  if (wip) {
    return (
      <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
        <PageWrapper label="Mesure d'audience" actions={null} hideLanguageSelector>
          <p style={{ marginTop: '30px' }}>
            Cette page est en cours de construction, elle est momentanément inaccessible.
          </p>
          <p>Merci de votre patience.</p>
        </PageWrapper>
      </div>
    );
  }

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      {disable && (
        <div className={styles.absoluteMessageContainer}>
          <p className={styles.messageTitle}>Page désactivée</p>
          <p className={styles.message}>
            Vous n'avez pas activé les fonctions de mesure d'audience dans votre contrat de
            maintenance. Vous ne pouvez donc pas avoir accès aux données de suivi d'audience.
            <br />
            <br />
            Pour plus d'informations ou pour activer cette option, contactez-nous !
          </p>
          <a className={styles.messageLink} href="mailto:contact@collectifnode.fr">
            Envoyer un mail
          </a>
        </div>
      )}
      <PageWrapper
        label="Mesure d'audience"
        actions={temporalityActions}
        hideLanguageSelector
        disable={disable}>
        {disable ? (
          // DISABLED ANALYTICS PAGE
          <div className={styles.scrollableSectionContainer}>
            <AnalyticsSection key={'1'} label="En bref" disable>
              <div className={styles.col3gridLayout}>
                {DISABLED_VALUES_BREF.map((c) => {
                  return (
                    <SmallerDataItem
                      key={c.key}
                      data={c.value}
                      label={c.label}
                      info={c.info}
                      format={c.format}
                      disabled
                    />
                  );
                })}
              </div>
            </AnalyticsSection>

            <AnalyticsSection key={'3'} label={'Détail des sessions'} disable>
              <div className={`${styles.gridLayout}`}>
                {DISABLED_VALUES_SECONDARY.map((dis_value, index) => (
                  <ChartContainer
                    label={dis_value.config.label}
                    type={dis_value.config.chartType}
                    data={dis_value.data}
                    config={dis_value.config}
                    key={index}
                  />
                ))}
              </div>
            </AnalyticsSection>
          </div>
        ) : (
          // ACTIVE ANALYTICS PAGE
          <div className={styles.scrollableSectionContainer}>
            {Object.entries(groupedCharts).map(([displayType, charts]) => {
              // Si aucune config pour ce type, on affiche rien
              if (charts.length === 0) return null;

              const sectionLabel = SECTION_LABELS[displayType] || displayType;

              if (displayType === 'summary') {
                return (
                  <AnalyticsSection key={displayType} label="En bref">
                    <div className={styles.col3gridLayout}>
                      {charts.map((c) => {
                        const value = c.data?.[c.dataKey] ?? null;

                        return (
                          <SmallerDataItem
                            key={c.key}
                            data={value}
                            label={c.label}
                            info={c.info}
                            format={c.format}
                          />
                        );
                      })}
                    </div>
                  </AnalyticsSection>
                );
              } else {
                return (
                  <AnalyticsSection key={displayType} label={sectionLabel}>
                    <div
                      className={`${displayType === 'general' ? styles.listLayout : styles.gridLayout}`}>
                      {charts.map((c) => (
                        <ChartContainer
                          label={c.label}
                          type={c.chartType}
                          data={c.data}
                          config={c}
                          isLoading={c.isLoading}
                          key={c.key}
                          info={c.info}
                        />
                      ))}
                    </div>
                  </AnalyticsSection>
                );
              }
            })}
            <div className={styles.textContentContainer}>
              <p className={styles.label}>
                Besoin d’une autre fonctionnalité ? Nous restons disponibles pour en discuter !
              </p>
              <a className={styles.link} href="mailto:contact@collectifnode.fr">
                contact@collectifnode.fr
              </a>
            </div>
          </div>
        )}
      </PageWrapper>
    </div>
  );
}

export default AnalyticsPage;
